import type { ReactNode } from 'react';
import type { Permission } from '@pm/shared';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/features/auth/permissions';
import { useSession, type Session } from '@/features/auth/session';
import { Card } from '@/ui/Card';
import { EmptyState } from '@/ui/States';
import { ShieldAlert } from 'lucide-react';

/**
 * Route-level gate. Nav already hides what a user cannot reach; this catches
 * a typed URL or a stale bookmark, and the API refuses it regardless.
 */
export function RequirePermission({ permissions, also, children }: {
  permissions: Permission[];
  /** A way in that is not a permission, such as leading a department. */
  also?: (session: Session) => boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { canAny, isReady } = usePermissions();
  const { data: session } = useSession();

  if (!isReady) return null;
  if (canAny(...permissions) || (session && also?.(session))) return <>{children}</>;

  return (
    <Card>
      <EmptyState
        icon={<ShieldAlert className="size-5" aria-hidden />}
        title={t('errors.forbiddenTitle')}
      />
    </Card>
  );
}
